/**
 * Helper centralizado para Toasts modernos
 * Garante que todos os toasts usem o estilo moderno da imagem 2
 * Layout: 25% ícone + 75% texto
 */
import { toast } from "react-toastify";
import React from "react";

// Classe central para todos os toasts modernos
const MODERN_TOAST_CLASS = "modern-toast-v2";

// Ícone de erro customizado
const ErrorIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Estilos inline COMPLETOS para garantir aplicação (força máxima)
// Usa variáveis CSS que se adaptam ao modo claro/escuro
// Não definir cores fixas aqui, deixar o CSS gerenciar via variáveis
const errorToastInlineStyle = {
  background: "var(--toast-error-bg)", // Usa variável CSS
  color: "var(--toast-error-text)", // Usa variável CSS
  borderRadius: "12px",
  padding: "16px 6px 16px 6px",
  fontSize: "14px",
  fontWeight: 400,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  border: "none",
  minHeight: "72px",
  display: "flex",
  flexDirection: "row", // Layout horizontal
  alignItems: "center",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  width: "auto",
  maxWidth: "600px",
  minWidth: "400px",
};

// Configuração base para todos os toasts
const baseToastConfig = {
  className: MODERN_TOAST_CLASS,
  bodyClassName: `${MODERN_TOAST_CLASS}__body`,
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  position: "top-center",
};

/**
 * Toast de ERRO moderno - FORÇA ESTILOS INLINE
 * @param {string} message - Mensagem de erro
 * @param {object} options - Opções adicionais
 */
export const showErrorToast = (message, options = {}) => {
  // Gerar toastId único baseado na mensagem para evitar duplicatas
  const defaultToastId = `error-${message.substring(0, 50).replace(/\s+/g, '-').toLowerCase()}`;
  
  // Dividir mensagem em título e subtítulo se houver ponto
  const parts = message.split('. ');
  const title = parts[0] + (parts.length > 1 ? '.' : '');
  const subtitle = parts.length > 1 ? parts.slice(1).join('. ') : '';
  
  // Renderizar conteúdo customizado com layout 25% ícone + 75% texto
  const toastContent = (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      color: '#000000',
      gap: '0',
    }}>
      {/* Área do ícone - 20% */}
      <div style={{
        width: '20%',
        minWidth: '80px',
        maxWidth: '120px',
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
      }}>
        {/* Blobs decorativos */}
        <div style={{
          position: 'absolute',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'rgba(239, 68, 68, 0.2)',
          zIndex: 1,
        }} />
        <div style={{
          position: 'absolute',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'rgba(239, 68, 68, 0.15)',
          zIndex: 2,
        }} />
        {/* Círculo branco com ícone */}
        <div style={{
          position: 'relative',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          zIndex: 3,
        }}>
          <ErrorIcon />
        </div>
      </div>
      
      {/* Área do texto - 80% */}
      <div style={{ 
        width: '80%',
        paddingLeft: '8px', // Reduzir pela metade (de 16px para 8px)
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'flex-start',
        flex: 1,
        color: '#000000',
      }}>
        <div style={{ 
          fontWeight: 700, 
          fontSize: '15px', 
          lineHeight: '1.4', 
          marginBottom: subtitle ? '4px' : '0',
          color: '#000000',
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ 
            fontWeight: 400, 
            fontSize: '14px', 
            lineHeight: '1.4',
            color: '#000000',
          }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
  
  return toast.error(toastContent, {
    ...baseToastConfig,
    className: `${MODERN_TOAST_CLASS} ${MODERN_TOAST_CLASS}--error`,
    toastId: options.toastId || defaultToastId,
    style: errorToastInlineStyle, // Estilos inline forçados
    bodyStyle: {
      margin: 0,
      padding: 0,
      color: '#000000',
      width: '100%',
    },
    icon: false, // Desabilitar ícone padrão do React-Toastify
    ...options,
  });
};

// Ícone de sucesso customizado
const SuccessIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 6L9 17L4 12" stroke="#198754" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Toast de SUCESSO moderno - FORÇA ESTILOS INLINE
 * @param {string} message - Mensagem de sucesso
 * @param {object} options - Opções adicionais
 */
export const showSuccessToast = (message, options = {}) => {
  // Gerar toastId único baseado na mensagem para evitar duplicatas
  const defaultToastId = `success-${message.substring(0, 50).replace(/\s+/g, '-').toLowerCase()}`;
  
  // Dividir mensagem em título e subtítulo se houver ponto
  const parts = message.split('. ');
  const title = parts[0] + (parts.length > 1 ? '.' : '');
  const subtitle = parts.length > 1 ? parts.slice(1).join('. ') : '';
  
  // Renderizar conteúdo customizado com layout 20% ícone + 80% texto
  const toastContent = (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      gap: '0',
    }}>
      {/* Área do ícone - 20% */}
      <div style={{
        width: '20%',
        minWidth: '80px',
        maxWidth: '120px',
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
      }}>
        {/* Blobs decorativos */}
        <div style={{
          position: 'absolute',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'rgba(25, 135, 84, 0.2)',
          zIndex: 1,
        }} />
        <div style={{
          position: 'absolute',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'rgba(25, 135, 84, 0.15)',
          zIndex: 2,
        }} />
        {/* Círculo branco com ícone */}
        <div style={{
          position: 'relative',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          zIndex: 3,
        }}>
          <SuccessIcon />
        </div>
      </div>
      
      {/* Área do texto - 80% */}
      <div style={{ 
        width: '80%',
        paddingLeft: '8px',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'flex-start',
        flex: 1,
      }}>
        <div style={{ 
          fontWeight: 700, 
          fontSize: '15px', 
          lineHeight: '1.4', 
          marginBottom: subtitle ? '4px' : '0',
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ 
            fontWeight: 400, 
            fontSize: '14px', 
            lineHeight: '1.4',
          }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
  
  return toast.success(toastContent, {
    ...baseToastConfig,
    className: `${MODERN_TOAST_CLASS} ${MODERN_TOAST_CLASS}--success`,
    toastId: options.toastId || defaultToastId,
    style: {
      background: "var(--toast-success-bg)",
      color: "var(--toast-success-text)",
      borderRadius: "12px",
      padding: "16px 6px 16px 6px",
      fontSize: "14px",
      fontWeight: 400,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      border: "1px solid var(--toast-success-border, transparent)",
      minHeight: "72px",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      width: "auto",
      maxWidth: "600px",
      minWidth: "400px",
    },
    bodyStyle: {
      margin: 0,
      padding: 0,
      width: '100%',
    },
    icon: false, // Desabilitar ícone padrão do React-Toastify
    ...options,
  });
};

// Ícone de informação customizado
const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 16V12M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#0dcaf0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Ícone de aviso customizado
const WarningIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#ffc107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 8V8.01" stroke="#ffc107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Função auxiliar para criar conteúdo de toast com layout 20% + 80%
 */
const createToastContent = (message, icon, iconColor, blobColor) => {
  // Dividir mensagem em título e subtítulo se houver ponto
  const parts = message.split('. ');
  const title = parts[0] + (parts.length > 1 ? '.' : '');
  const subtitle = parts.length > 1 ? parts.slice(1).join('. ') : '';
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      gap: '0',
    }}>
      {/* Área do ícone - 20% */}
      <div style={{
        width: '20%',
        minWidth: '80px',
        maxWidth: '120px',
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
      }}>
        {/* Blobs decorativos */}
        <div style={{
          position: 'absolute',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: blobColor,
          zIndex: 1,
        }} />
        <div style={{
          position: 'absolute',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: blobColor,
          opacity: 0.75,
          zIndex: 2,
        }} />
        {/* Círculo branco com ícone */}
        <div style={{
          position: 'relative',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          zIndex: 3,
        }}>
          {icon}
        </div>
      </div>
      
      {/* Área do texto - 80% */}
      <div style={{ 
        width: '80%',
        paddingLeft: '8px',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'flex-start',
        flex: 1,
      }}>
        <div style={{ 
          fontWeight: 700, 
          fontSize: '15px', 
          lineHeight: '1.4', 
          marginBottom: subtitle ? '4px' : '0',
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ 
            fontWeight: 400, 
            fontSize: '14px', 
            lineHeight: '1.4',
          }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Toast de INFORMAÇÃO moderno - FORÇA ESTILOS INLINE
 * @param {string} message - Mensagem informativa
 * @param {object} options - Opções adicionais
 */
export const showInfoToast = (message, options = {}) => {
  const defaultToastId = `info-${message.substring(0, 50).replace(/\s+/g, '-').toLowerCase()}`;
  
  const toastContent = createToastContent(
    message,
    <InfoIcon />,
    'rgba(13, 202, 240, 0.2)',
    'rgba(13, 202, 240, 0.2)'
  );
  
  return toast.info(toastContent, {
    ...baseToastConfig,
    className: `${MODERN_TOAST_CLASS} ${MODERN_TOAST_CLASS}--info`,
    toastId: options.toastId || defaultToastId,
    style: {
      background: "var(--toast-info-bg)",
      color: "var(--toast-info-text)",
      borderRadius: "12px",
      padding: "16px 6px 16px 6px",
      fontSize: "14px",
      fontWeight: 400,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      border: "1px solid var(--toast-info-border, transparent)",
      minHeight: "72px",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      width: "auto",
      maxWidth: "600px",
      minWidth: "400px",
    },
    bodyStyle: {
      margin: 0,
      padding: 0,
      width: '100%',
    },
    icon: false,
    ...options,
  });
};

/**
 * Toast de AVISO moderno - FORÇA ESTILOS INLINE
 * @param {string} message - Mensagem de aviso
 * @param {object} options - Opções adicionais
 */
export const showWarningToast = (message, options = {}) => {
  const defaultToastId = `warning-${message.substring(0, 50).replace(/\s+/g, '-').toLowerCase()}`;
  
  const toastContent = createToastContent(
    message,
    <WarningIcon />,
    'rgba(255, 193, 7, 0.2)',
    'rgba(255, 193, 7, 0.2)'
  );
  
  return toast.warning(toastContent, {
    ...baseToastConfig,
    className: `${MODERN_TOAST_CLASS} ${MODERN_TOAST_CLASS}--warning`,
    toastId: options.toastId || defaultToastId,
    style: {
      background: "var(--toast-warning-bg)",
      color: "var(--toast-warning-text)",
      borderRadius: "12px",
      padding: "16px 6px 16px 6px",
      fontSize: "14px",
      fontWeight: 400,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      border: "1px solid var(--toast-warning-border, transparent)",
      minHeight: "72px",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      width: "auto",
      maxWidth: "600px",
      minWidth: "400px",
    },
    bodyStyle: {
      margin: 0,
      padding: 0,
      width: '100%',
    },
    icon: false,
    ...options,
  });
};

// Exportar classe para uso em CSS
export { MODERN_TOAST_CLASS };
